/**
 * Poll while the page is visible and the browser reports a connection.
 * Schedule from completion so slow requests cannot overlap. Returning from a
 * hidden/offline state refreshes immediately instead of waiting a full interval.
 */
export function startActivePolling(callback, intervalMs, { runImmediately = false } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const delay = Number.isFinite(Number(intervalMs)) && Number(intervalMs) > 0
    ? Number(intervalMs)
    : 15000;
  let stopped = false;
  let running = false;
  let resumePending = false;
  let timer;

  const isActive = () => !document.hidden && window.navigator.onLine !== false;
  let wasActive = isActive();

  function clearTimer() {
    window.clearTimeout(timer);
    timer = undefined;
  }

  function schedule() {
    clearTimer();
    if (!stopped && isActive()) {
      timer = window.setTimeout(run, delay);
    }
  }

  function settle() {
    running = false;
    if (stopped) {
      return;
    }
    if (resumePending && isActive()) {
      resumePending = false;
      run();
    } else {
      schedule();
    }
  }

  function run() {
    clearTimer();
    if (stopped || !isActive()) {
      return;
    }
    if (running) {
      resumePending = true;
      return;
    }

    resumePending = false;
    running = true;
    let result;
    try {
      result = callback();
    } catch {
      settle();
      return;
    }
    // Consumers handle their own error UI. Consume failures here as well so a
    // background refresh never produces an unhandled rejection or stops polling.
    Promise.resolve(result).then(settle, settle);
  }

  function handleActivityChange() {
    const active = isActive();
    if (!active) {
      clearTimer();
    } else if (!wasActive) {
      run();
    }
    wasActive = active;
  }

  document.addEventListener('visibilitychange', handleActivityChange);
  window.addEventListener('online', handleActivityChange);
  window.addEventListener('offline', handleActivityChange);

  if (runImmediately) {
    run();
  } else {
    schedule();
  }

  return () => {
    stopped = true;
    resumePending = false;
    clearTimer();
    document.removeEventListener('visibilitychange', handleActivityChange);
    window.removeEventListener('online', handleActivityChange);
    window.removeEventListener('offline', handleActivityChange);
  };
}
